import React from 'react';
import { ShimmerLoader, Typography } from '@guildeducationinc/recess';
import { useGetPathwayQuery } from '@src/graphqlTypes/graphqlGatewayTypes';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { logDataDogError } from '@src/utilities/datadog';
import { CAREER_PATHWAYS_URL } from '@src/utilities/envConfig';
import { useDecision } from '@optimizely/react-sdk';
import { getRolesToDisplay, ROLE_MAPPING, RoleVariaions } from '@src/constants/featureFlags';
import { trackEvent } from '@src/services/analytics';
import { segmentCase } from '@guildeducationinc/catalog-tools';
import { PathwayWithRoles } from '../CareerPathways';
import styles from './Pathway.module.scss';

interface PathwayProps {
	pathway: PathwayWithRoles;
}

export const Pathway: React.FC<PathwayProps> = ({ pathway }) => {
	const { pathwayId, roles } = pathway;
	const { employerUuid } = useSearchContext();
	const pathwayUrl = `${CAREER_PATHWAYS_URL}${pathwayId}`;
	const [rolesDecision] = useDecision(ROLE_MAPPING);

	const {
		data: pathwayResponse,
		loading,
		error,
	} = useGetPathwayQuery({
		context: {
			gqlServer: 'gateway',
		},
		variables: {
			employerId: employerUuid,
			pathwayId,
		},
		onError: error => {
			logDataDogError(error, 'PathwayDataError');
		},
	});

	if (error || pathwayResponse?.pathway === null) return null;

	const genericRolesWithoutDuplicates = roles.reduce(
		(uniqueRoles, role) => {
			if (!uniqueRoles.some(uniqueRole => uniqueRole.name === role.genericName)) {
				uniqueRoles.push({ id: role.id, name: role.genericName });
			}
			return uniqueRoles;
		},
		[] as { id: string; name: string }[]
	);

	const rolesToDisplay =
		getRolesToDisplay(rolesDecision) === RoleVariaions.GENERIC
			? genericRolesWithoutDuplicates
			: roles;

	const handleClick = () => {
		trackEvent('Clickable', {
			tag: `career_pathways_${pathwayResponse?.pathway?.content.title}`,
			text: `${segmentCase(pathwayResponse?.pathway?.content.title)}`,
			type: 'link',
		});
	};

	return (
		<ShimmerLoader height={32} label='career pathway roles' loaded={!loading}>
			<div className={styles.pathway}>
				<img alt='' className={styles.img} src={pathwayResponse?.pathway?.content.heroImage}></img>
				<div className={styles.pathwayBody}>
					<Typography
						Component='a'
						className={styles.pathwayLink}
						gutterBottom='sm'
						href={pathwayUrl}
						onClick={handleClick}
						variant='h5'
					>
						{pathwayResponse?.pathway?.content.title}
					</Typography>
					<ul>
						{rolesToDisplay.map(role => {
							return (
								<Typography Component='li' key={role.id} variant='body1'>
									{role.name}
								</Typography>
							);
						})}
					</ul>
				</div>
			</div>
		</ShimmerLoader>
	);
};
